import { useCallback, useEffect, useState } from 'react';

import { ToggleButton } from '../../../../components/ToggleButton/ToggleButton';
import {
  REFERRAL_AVATAR_BY_RANK,
  ReferralLeaderboardEntry,
  ReferralLeaderboardMode,
} from '../../../../models/referral/referral.model';
import ReferralTermsModal from './ReferralTermsModal';
import {
  Card,
  EmptyState,
  HeaderMeta,
  LeaderboardHeader,
  LeaderboardTable,
  LowerGrid,
  Podium,
  PodiumBlock,
  PodiumColumn,
  PrizeCarouselFrame,
  PrizeDot,
  PrizeDots,
  PrizeImage,
  PrizeLink,
  PrizePanel,
  PrizeText,
  SectionHeading,
  TableHeader,
  TableRow,
  ToggleLabel,
  Avatar,
  PrimaryActionButton,
} from '../Referral.styles';

interface ReferralLeaderboardProps {
  entries: ReferralLeaderboardEntry[];
  mode: ReferralLeaderboardMode;
  loading?: boolean;
  onModeChange: (mode: ReferralLeaderboardMode) => void;
  error?: string | null;
}

const podiumOrder = [2, 1, 3];
const rewardItems = [
  {
    src: '/static/img/referrals/cash-prize.png',
    alt: 'Cash prize reward',
  },
  {
    src: '/static/img/referrals/reward-watch.jpg',
    alt: 'Smart watch reward',
  },
  {
    src: '/static/img/referrals/reward-powerbank.jpg',
    alt: 'Power bank reward',
  },
  {
    src: '/static/img/referrals/reward-earbuds.jpg',
    alt: 'Wireless earbuds reward',
  },
];

const ReferralLeaderboard: React.FC<ReferralLeaderboardProps> = ({
  entries,
  mode,
  loading,
  onModeChange,
  error,
}) => {
  const [activeRewardIndex, setActiveRewardIndex] = useState(0);
  const [showTerms, setShowTerms] = useState(false);
  const closeTerms = useCallback(() => setShowTerms(false), []);

  const handleToggle = () => {
    const nextMode = mode === 'monthly' ? 'overall' : 'monthly';
    onModeChange(nextMode);
  };

  const podiumEntries = podiumOrder.map((rank) =>
    entries.find((entry) => entry.rank === rank)
  );

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveRewardIndex((current) => (current + 1) % rewardItems.length);
    }, 3500);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <Card>
      <LeaderboardHeader>
        <SectionHeading>Leaderboard</SectionHeading>
        <HeaderMeta>
          <ToggleLabel>
            <ToggleButton
              controller={handleToggle}
              defaultState={mode === 'monthly'}
              loading={loading}
            />
            Monthly Leaderboard
          </ToggleLabel>
        </HeaderMeta>
      </LeaderboardHeader>

      <Podium>
        {podiumEntries.map((entry, index) => {
          const rank = podiumOrder[index];
          const isFirst = rank === 1;

          return (
            <PodiumColumn key={rank} elevated={isFirst}>
              <Avatar
                src={entry?.avatarUrl || REFERRAL_AVATAR_BY_RANK[rank]}
                alt={`Rank ${rank}`}
              />
              <PodiumBlock elevated={isFirst}>{rank}</PodiumBlock>
            </PodiumColumn>
          );
        })}
      </Podium>

      <LowerGrid>
        <LeaderboardTable>
          <TableHeader>
            <div>Rank</div>
            <div>Email Address</div>
            <div>Points</div>
          </TableHeader>
          {entries.length === 0 ? (
            <EmptyState>
              {loading ? 'Loading leaderboard...' : error || `No qualified referrals on the ${mode === 'monthly' ? 'leaderboard yet this month' : 'overall leaderboard yet'}.`}
              {error && <PrimaryActionButton type="button" disabled={loading} onClick={() => onModeChange(mode)}>Try again</PrimaryActionButton>}
            </EmptyState>
          ) : (
            <>
              {entries.map((entry) => (
                <TableRow key={`${entry.rank}-${entry.maskedEmail}`}>
                  <div>{entry.rank}</div>
                  <div>{entry.maskedEmail}</div>
                  <div>
                    {entry.points} ({entry.referralCount} user
                    {entry.referralCount === 1 ? '' : 's'})
                  </div>
                </TableRow>
              ))}
            </>
          )}
        </LeaderboardTable>

        <PrizePanel>
          <PrizeCarouselFrame>
            <PrizeImage
              src={rewardItems[activeRewardIndex].src}
              alt={rewardItems[activeRewardIndex].alt}
            />
          </PrizeCarouselFrame>
          <PrizeDots>
            {rewardItems.map((item, index) => (
              <PrizeDot
                key={item.src}
                type="button"
                active={index === activeRewardIndex}
                onClick={() => setActiveRewardIndex(index)}
                aria-label={`Show reward item ${index + 1}`}
              />
            ))}
          </PrizeDots>
          <PrizeText>win any of our monthly prizes</PrizeText>
          <PrizeLink type="button" onClick={() => setShowTerms(true)} aria-haspopup="dialog">
            terms and conditions apply
          </PrizeLink>
        </PrizePanel>
      </LowerGrid>
      {showTerms ? <ReferralTermsModal onClose={closeTerms} /> : null}
    </Card>
  );
};

export default ReferralLeaderboard;
